import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";

import {
  createFastenerToolingMapping,
  deleteFastenerToolingMapping,
  getFastenerToolingMapping,
  listFastenerToolingMapping,
  updateFastenerToolingMapping,
  type CreateFastenerToolingMappingBody,
  type FastenerToolingMapping,
  type FastenerToolingMappingKey,
  type ListFastenerToolingMappingParams,
  type PaginatedFastenerToolingMappings,
  type UpdateFastenerToolingMappingBody,
} from "../api/fastenerToolingMapping";

export function useFastenerToolingMappings(params: ListFastenerToolingMappingParams) {
  return useQuery<PaginatedFastenerToolingMappings>({
    queryKey: ["fastener-tooling-mapping", "list", params],
    queryFn: () => listFastenerToolingMapping(params),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function useFastenerToolingMapping(key: FastenerToolingMappingKey | null) {
  return useQuery<FastenerToolingMapping>({
    queryKey: ["fastener-tooling-mapping", "detail", key?.partNo, key?.toolingSetId],
    queryFn: () => getFastenerToolingMapping(key as FastenerToolingMappingKey),
    enabled: key !== null,
    staleTime: 30_000,
  });
}

export function useCreateFastenerToolingMapping() {
  const queryClient = useQueryClient();
  return useMutation<FastenerToolingMapping, Error, CreateFastenerToolingMappingBody>({
    mutationFn: (body) => createFastenerToolingMapping(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["fastener-tooling-mapping"] });
    },
  });
}

export function useUpdateFastenerToolingMapping() {
  const queryClient = useQueryClient();
  return useMutation<
    FastenerToolingMapping,
    Error,
    { oldKey: FastenerToolingMappingKey; body: UpdateFastenerToolingMappingBody }
  >({
    mutationFn: ({ oldKey, body }) => updateFastenerToolingMapping(oldKey, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["fastener-tooling-mapping"] });
    },
  });
}

export function useDeleteFastenerToolingMapping() {
  const queryClient = useQueryClient();
  return useMutation<void, Error, FastenerToolingMappingKey>({
    mutationFn: (key) => deleteFastenerToolingMapping(key),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["fastener-tooling-mapping"] });
    },
  });
}
